import { PROBLEM_STATUS_DISPLAY_NAMES } from '@yandex-int/k-common/constants';
import { ClessonResults, LESSON_STATUS, ProblemProgress } from '@yandex-int/k-common/typings';
import { format } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';

import { getStudents } from 'common.components/classes/selectors';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import { getSelectedProblemLink } from 'common.components/lesson-view.store/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import * as problemsViewSelectors from 'store/problems-view/selectors';
import { isOpenAnswer, getProblemMap } from 'store/problems/utils';
import {
    filterFailStacks,
    filterTheoryStacks,
    filterSuccessStacks,
    getPerformanceProblemLink,
} from 'store/skills-lesson-problems/utils';
import { getFormattedTime } from 'utils/get-formatted-time';
import { getOpenAnswerUrl } from 'utils/open-answer';

import { STATS_EMPTY_STATE_MODE, STATS_MODE } from './typings';

export const getStatsMode = createSelector(
    [problemsViewSelectors.getSelectedProblemId, lessonViewSelectors.getSelectedStudentId],
    (selectedProblemId, selectedStudentId) => {
        if (selectedProblemId) {
            return STATS_MODE.PROBLEM;
        }

        if (selectedStudentId) {
            return STATS_MODE.STUDENT;
        }

        return STATS_MODE.LESSON;
    }
);

export const getAssignedStudents = createSelector(
    [getStudents, lessonSelectors.getClessonResults],
    (students, clessonResults) => {
        const studentIds = students?.map((student) => student.id);

        return (
            studentIds?.filter((studentId) => {
                const studentProblems = clessonResults?.students?.[studentId]?.problems;

                return Boolean(studentProblems && Object.keys(studentProblems).length);
            }) || []
        );
    }
);

export const getStartedStudents = createSelector(
    [getAssignedStudents, lessonSelectors.getClessonResults],
    (students, clessonResults) => {
        return students.filter((studentId) => {
            const studentAttempts = clessonResults?.students?.[studentId]?.attempts;

            return Boolean(studentAttempts);
        });
    }
);

const calculatePerformancePercent = ({ correct, max }: { correct: number; max: number }) => {
    return correct && max && Number(correct) / Number(max);
};

export const getStudentLessonPerformanceCalcucator = createSelector(
    [lessonSelectors.getEvaluableProblemsNumber],
    (evaluableProblemsNumber) => (studentId: number, clessonResults: ClessonResults) => {
        const result = clessonResults.students[studentId];

        return calculatePerformancePercent({ correct: result.correctNumber, max: evaluableProblemsNumber });
    }
);

const getStudentCustomFlowLessonPerformanceCalculator = createSelector(
    [lessonSelectors.getLesson, lessonSelectors.getMainStacksFromLessonStructure],
    (lesson, mainStacks) => {
        if (!lesson || isEmpty(mainStacks)) {
            return null;
        }
        const problemMap = getProblemMap(lesson);
        return (studentId: number, clessonResults: ClessonResults) => {
            const result = clessonResults?.students[studentId];
            const studentProblemResult = result?.problems;
            if (!studentProblemResult || isEmpty(studentProblemResult) || !result) {
                return null;
            }

            const stats = mainStacks.reduce(
                (acc, stack) => {
                    const problemLink = getPerformanceProblemLink(stack, problemMap, studentProblemResult);
                    if (!problemLink) {
                        return acc;
                    }
                    const { status } = studentProblemResult[problemLink.id];

                    return {
                        correct: acc.correct + Number(status === PROBLEM_STATUS_DISPLAY_NAMES.CORRECT),
                        max: acc.max + 1,
                    };
                },
                { correct: 0, max: 0 }
            );

            return calculatePerformancePercent(stats);
        };
    }
);

const getStudentPerformanceCalculator = (storeState: any) => {
    const isCustomFlowLesson = lessonSelectors.getIsCustomFlowLesson(storeState);
    if (isCustomFlowLesson) {
        return (
            getStudentCustomFlowLessonPerformanceCalculator(storeState) ||
            getStudentLessonPerformanceCalcucator(storeState)
        );
    }

    return getStudentLessonPerformanceCalcucator(storeState);
};

export const getPerformancePercent = createSelector(
    [getStartedStudents, lessonSelectors.getClessonResults, getStudentPerformanceCalculator],
    (students, clessonResults, calculateStudentPerformance) => {
        if (students.length === 0 || !clessonResults) {
            return 0;
        }

        const studentsPerformance = students.reduce((acc, studentId) => {
            const studentPercent = calculateStudentPerformance(studentId, clessonResults);

            return studentPercent ? acc + studentPercent : acc;
        }, 0);

        return Math.round((studentsPerformance / students.length) * 100);
    }
);

export const getAverageLessonTime = createSelector(
    [getStartedStudents, lessonSelectors.getClessonResults],
    (students, clessonResults) => {
        if (students.length === 0) {
            return '';
        }

        const timeSum = students.reduce((acc, studentId) => {
            const studentTime = clessonResults?.students?.[studentId]?.time;

            return studentTime ? acc + Number(studentTime) : acc;
        }, 0);

        return getFormattedTime(timeSum / students.length);
    }
);

export const getAreLessonStatsEmpty = createSelector(
    [lessonSelectors.getIsLessonEvaluated, getStartedStudents, lessonSelectors.getClessonResults],
    (isEvaluated, students, clessonResults) => {
        if (!isEvaluated) {
            return true;
        }

        return !students.some((studentId) => {
            const studentAttempts = clessonResults?.students?.[studentId]?.attempts;

            return studentAttempts !== 0;
        });
    }
);

export const getEmptyStateDeadlineDate = createSelector(
    [lessonSelectors.getLessonDeadlineDate, lessonSelectors.getLessonEvaluationDate],
    (dateDeadline, evaluationDate) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return evaluationDate ? evaluationDate : new Date(dateDeadline!);
    }
);

export const getEmptyStateMode = createSelector(
    [
        getStatsMode,
        lessonSelectors.getLessonStatus,
        lessonSelectors.getIsLessonEvaluated,
        lessonSelectors.getIsLessonEvaluable,
    ],
    (
        // eslint-disable-line complexity
        statsMode,
        status,
        isEvaluated,
        isEvaluable
    ) => {
        // eslint-disable-line max-params
        const isProblemMode = statsMode === STATS_MODE.PROBLEM;

        let mode;

        if (!isEvaluated && isEvaluable) {
            mode = STATS_EMPTY_STATE_MODE.NOT_EVALUATED;
            // values = { date: format(date, 'dd.MM'), time: format(date, 'HH:mm') };
        } else {
            switch (status) {
                case LESSON_STATUS.ASSIGNED:
                case LESSON_STATUS.ASSIGNED_IN_FUTURE:
                case LESSON_STATUS.IN_PROGRESS:
                    mode = isProblemMode
                        ? STATS_EMPTY_STATE_MODE.PROBLEM_IN_PROGRESS
                        : STATS_EMPTY_STATE_MODE.LESSON_IN_PROGRESS;

                    break;
                case LESSON_STATUS.FINISH_FORCED:
                    mode = STATS_EMPTY_STATE_MODE.FINISH_FORCED;

                    break;
                default:
                    mode = STATS_EMPTY_STATE_MODE.NOT_SOLVED;
            }
        }

        return mode;
    }
);

export const getProblemResult = createSelector(
    [lessonViewSelectors.getSelectedProblemLinkId, lessonSelectors.getClessonResults],
    (problemId, clessonResults) => {
        if (!problemId) {
            return null;
        }

        return clessonResults?.problems[problemId] || null;
    }
);

export const getProblemStats = createSelector([getProblemResult], (problemResult) => {
    return problemResult?.progress || null;
});

export const getIsProblemDifficult = createSelector([getProblemResult], (problemResult) => {
    return problemResult?.details?.isDifficult || false;
});

export const getAverageProblemTime = createSelector(
    [lessonViewSelectors.getSelectedProblemLinkId, lessonSelectors.getClessonResults],
    (problemId, clessonResults) => {
        if (!problemId) {
            return '';
        }

        const averageTime = clessonResults?.problems?.[problemId]?.details.medianTime || 0;

        return format(Number(averageTime) * 1000, 'mm:ss');
    }
);

export const getProblemViewsCount = createSelector(
    [lessonViewSelectors.getSelectedProblemLinkId, lessonSelectors.getClessonResults],
    (problemId, clessonResults) => {
        if (!problemId || !clessonResults) {
            return 0;
        }

        return Object.values(clessonResults.students).reduce((counter, studentResult) => {
            if (studentResult.problems[problemId]?.time > 0) {
                counter += 1;
            }

            return counter;
        }, 0);
    }
);

export const getAnsweredProblemsCount = createSelector(
    [lessonViewSelectors.getSelectedProblemLinkId, lessonSelectors.getClessonResults],
    (problemId, clessonResults) => {
        if (!problemId || !clessonResults) {
            return 0;
        }

        return Object.values(clessonResults.students).reduce((counter, studentResult) => {
            if (getOpenAnswerUrl(studentResult.problems[problemId])) {
                counter += 1;
            }

            return counter;
        }, 0);
    }
);

export const getAreProblemStatsEmpty = createSelector(
    [
        lessonSelectors.getIsLessonEvaluable,
        lessonSelectors.getIsLessonEvaluated,
        getAreLessonStatsEmpty,
        getProblemStats,
        lessonViewSelectors.getIsTheoryProblemSelected,
        getProblemViewsCount,
        getSelectedProblemLink,
        getAnsweredProblemsCount,
    ],
    // eslint-disable-next-line complexity
    (
        isEvaluable,
        isEvaluated,
        areLessonStatsEmpty,
        problemStats,
        isTheory,
        problemViewsCount,
        selectedProblem,
        answeredProblemsCount
        // eslint-disable-next-line max-params
    ) => {
        if (isTheory) {
            return !problemViewsCount;
        }

        if (isOpenAnswer(selectedProblem?.problem || null)) {
            if (isEvaluable && !isEvaluated) {
                return true;
            }

            return !answeredProblemsCount;
        }

        if (areLessonStatsEmpty) {
            return true;
        }

        if (!isEvaluated) {
            return true;
        }

        if (!problemStats) {
            return false;
        }

        return problemStats.fail === 0 && problemStats.success === 0;
    }
);

export const getAreStudentStatsEmpty = createSelector(
    [lessonSelectors.getClessonResults, lessonSelectors.getIsLessonEvaluated, lessonViewSelectors.getSelectedStudentId],
    (clessonResults, isEvaluated, studentId) => {
        if (!studentId) {
            return true;
        }

        if (!isEvaluated) {
            return true;
        }

        const seenProbemsNumber = clessonResults?.students[studentId].seenNumber;

        return !seenProbemsNumber;
    }
);

export const getStudentTime = createSelector(
    [lessonSelectors.getClessonResults, lessonViewSelectors.getSelectedStudentId],
    (clessonResults, studentId) => {
        if (!studentId) {
            return '';
        }

        const time = clessonResults?.students[studentId].time;

        return format(Number(time) * 1000, 'mm:ss');
    }
);

export const getStudentPoints = createSelector(
    [lessonSelectors.getClessonResults, lessonViewSelectors.getSelectedStudentId],
    (clessonResults, studentId) => {
        if (!studentId || !clessonResults) {
            return '—';
        }

        return clessonResults.students[studentId].points;
    }
);

export const getCommonStudentStats = createSelector(
    [
        lessonSelectors.getClessonResults,
        lessonViewSelectors.getSelectedStudentId,
        lessonSelectors.getEvaluableProblemsNumber,
    ],
    (clessonResults, studentId, evaluableProblemsNumber) => {
        if (!studentId || !clessonResults) {
            return null;
        }

        const { correctNumber: success, incorrectNumber: fail } = clessonResults.students[studentId];

        return {
            fail,
            success,
            unknown: evaluableProblemsNumber - fail - success,
        };
    }
);

export const getStudentCustomLessonStats = createSelector(
    [
        lessonSelectors.getClessonResults,
        lessonViewSelectors.getSelectedStudentId,
        lessonSelectors.getLesson,
        lessonSelectors.getMainStacksFromLessonStructure,
    ],
    (clessonResults, studentId, lesson, mainStacks): ProblemProgress | null => {
        if (!studentId || !lesson || !mainStacks || !clessonResults) {
            return null;
        }
        const result = clessonResults.students[studentId];
        const studentProblemResult = result?.problems;
        const problemMap = getProblemMap(lesson);
        const theoryStacks = filterTheoryStacks(mainStacks, problemMap);
        const successStacks = filterSuccessStacks(mainStacks, problemMap, studentProblemResult);
        const failStacks = filterFailStacks(mainStacks, problemMap, studentProblemResult);
        const success = successStacks.length;
        const fail = failStacks.length;

        return {
            success,
            fail,
            unknown: mainStacks.length - success - fail - theoryStacks.length,
        };
    }
);

export const getStudentStats = (storeState: any) => {
    const isCustomFlowLesson = lessonSelectors.getIsCustomFlowLesson(storeState);
    if (isCustomFlowLesson) {
        return getStudentCustomLessonStats(storeState);
    }
    return getCommonStudentStats(storeState);
};
